/**
 * Miskulin Technologies — Universal Translation System (i18n)
 * Drop this file into any app's src/ folder to enable multi-language support.
 *
 * Usage:
 *   import { useTranslation, LanguageSelector } from './i18n';
 *   const { t, language, setLanguage } = useTranslation();
 *   <p>{t('dashboard')}</p>
 *   <LanguageSelector />
 *
 * Supports 30+ languages. Uses browser language detection by default.
 * Translations stored in localStorage for persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Core UI translations for all shared components across every app
const translations = {
  en: { langName: 'English', flag: '\u{1F1FA}\u{1F1F8}', dir: 'ltr',
    // Navigation & Layout
    dashboard: 'Dashboard', analytics: 'Analytics', reports: 'Reports', settings: 'Settings',
    profile: 'Profile', logout: 'Logout', login: 'Login', notifications: 'Notifications',
    customers: 'Customers', invoicing: 'Invoicing', workforce: 'Workforce', compliance: 'Compliance',
    equipment: 'Equipment', locations: 'Locations', overwatch: 'Overwatch', schedule: 'Schedule',
    inventory: 'Inventory', billing: 'Billing', staff: 'Staff', suppliers: 'Suppliers',
    // Florigen-specific
    generator: 'Generator', gallery: 'Gallery', mint: 'Mint NFT', collection: 'My Collection',
    materials: 'Materials', export: 'Export', preview: 'Preview', uploadLogo: 'Upload Logo',
    // Actions
    save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', add: 'Add', search: 'Search',
    filter: 'Filter', import: 'Import', refresh: 'Refresh', submit: 'Submit',
    create: 'Create', update: 'Update', close: 'Close', confirm: 'Confirm', back: 'Back',
    next: 'Next', previous: 'Previous', download: 'Download', upload: 'Upload', print: 'Print',
    // Status
    active: 'Active', inactive: 'Inactive', pending: 'Pending', approved: 'Approved',
    rejected: 'Rejected', completed: 'Completed', inProgress: 'In Progress', overdue: 'Overdue',
    draft: 'Draft', archived: 'Archived', live: 'Live', offline: 'Offline',
    // Common Labels
    name: 'Name', email: 'Email', phone: 'Phone', address: 'Address', date: 'Date',
    time: 'Time', status: 'Status', type: 'Type', description: 'Description', notes: 'Notes',
    total: 'Total', subtotal: 'Subtotal', tax: 'Tax', amount: 'Amount', price: 'Price',
    quantity: 'Quantity', unit: 'Unit', category: 'Category', priority: 'Priority',
    assignedTo: 'Assigned To', createdBy: 'Created By', lastUpdated: 'Last Updated',
    dueDate: 'Due Date', startDate: 'Start Date', endDate: 'End Date',
    // Messages
    noResults: 'No results found', loading: 'Loading...', error: 'An error occurred',
    success: 'Success', saved: 'Saved successfully', deleted: 'Deleted successfully',
    confirmDelete: 'Are you sure you want to delete this?', welcome: 'Welcome',
    noData: 'No data available', selectOption: 'Select an option',
    // Auth
    password: 'Password', forgotPassword: 'Forgot Password?', signIn: 'Sign In',
    signOut: 'Sign Out', signUp: 'Sign Up', rememberMe: 'Remember Me',
    changePassword: 'Change Password', newPassword: 'New Password',
    confirmPassword: 'Confirm Password', currentPassword: 'Current Password',
    tryDemo: 'Try Demo',
    // Profile
    personalInfo: 'Personal Information', company: 'Company', role: 'Role',
    language: 'Language', timezone: 'Timezone', theme: 'Theme',
  },
  es: { langName: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}', dir: 'ltr',
    dashboard: 'Panel', analytics: 'Anal\u00edtica', reports: 'Informes', settings: 'Configuraci\u00f3n',
    profile: 'Perfil', logout: 'Cerrar sesi\u00f3n', login: 'Iniciar sesi\u00f3n',
    generator: 'Generador', gallery: 'Galer\u00eda', mint: 'Acu\u00f1ar NFT', collection: 'Mi Colecci\u00f3n',
    materials: 'Materiales', uploadLogo: 'Subir Logo',
    save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar',
    name: 'Nombre', email: 'Correo', status: 'Estado', total: 'Total',
    noResults: 'Sin resultados', loading: 'Cargando...', welcome: 'Bienvenido',
    password: 'Contrase\u00f1a', signIn: 'Iniciar sesi\u00f3n', language: 'Idioma',
  },
  fr: { langName: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}', dir: 'ltr',
    dashboard: 'Tableau de bord', analytics: 'Analytique', reports: 'Rapports', settings: 'Param\u00e8tres',
    generator: 'G\u00e9n\u00e9rateur', gallery: 'Galerie', mint: 'Frapper NFT', collection: 'Ma Collection',
    materials: 'Mat\u00e9riaux', uploadLogo: 'T\u00e9l\u00e9verser Logo',
    save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer',
    name: 'Nom', email: 'Courriel', status: 'Statut',
    noResults: 'Aucun r\u00e9sultat', loading: 'Chargement...', welcome: 'Bienvenue',
    password: 'Mot de passe', signIn: 'Se connecter', language: 'Langue',
  },
  de: { langName: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}', dir: 'ltr',
    dashboard: '\u00dcbersicht', analytics: 'Analyse', reports: 'Berichte', settings: 'Einstellungen',
    generator: 'Generator', gallery: 'Galerie', mint: 'NFT pr\u00e4gen', collection: 'Meine Sammlung',
    save: 'Speichern', cancel: 'Abbrechen', delete: 'L\u00f6schen',
    name: 'Name', email: 'E-Mail', status: 'Status',
    noResults: 'Keine Ergebnisse', loading: 'Laden...', welcome: 'Willkommen',
    password: 'Passwort', signIn: 'Anmelden', language: 'Sprache',
  },
  zh: { langName: '\u4E2D\u6587', flag: '\u{1F1E8}\u{1F1F3}', dir: 'ltr',
    dashboard: '\u4EEA\u8868\u677F', analytics: '\u5206\u6790', reports: '\u62A5\u544A', settings: '\u8BBE\u7F6E',
    generator: '\u751F\u6210\u5668', gallery: '\u753B\u5ECA', mint: '\u94F8\u9020 NFT', collection: '\u6211\u7684\u6536\u85CF',
    save: '\u4FDD\u5B58', cancel: '\u53D6\u6D88', delete: '\u5220\u9664',
    name: '\u540D\u79F0', email: '\u7535\u5B50\u90AE\u4EF6', status: '\u72B6\u6001',
    noResults: '\u6CA1\u6709\u7ED3\u679C', loading: '\u52A0\u8F7D\u4E2D...', welcome: '\u6B22\u8FCE',
    password: '\u5BC6\u7801', signIn: '\u767B\u5F55', language: '\u8BED\u8A00',
  },
  ja: { langName: '\u65E5\u672C\u8A9E', flag: '\u{1F1EF}\u{1F1F5}', dir: 'ltr',
    dashboard: '\u30C0\u30C3\u30B7\u30E5\u30DC\u30FC\u30C9', generator: '\u30B8\u30A7\u30CD\u30EC\u30FC\u30BF\u30FC',
    gallery: '\u30AE\u30E3\u30E9\u30EA\u30FC', mint: 'NFT\u3092\u9020\u5E63',
    save: '\u4FDD\u5B58', cancel: '\u30AD\u30E3\u30F3\u30BB\u30EB',
    noResults: '\u7D50\u679C\u306A\u3057', loading: '\u8AAD\u307F\u8FBC\u307F\u4E2D...', welcome: '\u3088\u3046\u3053\u305D',
    password: '\u30D1\u30B9\u30EF\u30FC\u30C9', signIn: '\u30B5\u30A4\u30F3\u30A4\u30F3', language: '\u8A00\u8A9E',
  },
  ko: { langName: '\uD55C\uAD6D\uC5B4', flag: '\u{1F1F0}\u{1F1F7}', dir: 'ltr',
    dashboard: '\uB300\uC2DC\uBCF4\uB4DC', generator: '\uC0DD\uC131\uAE30',
    gallery: '\uAC24\uB7EC\uB9AC', mint: 'NFT \uBC1C\uD589',
    save: '\uC800\uC7A5', cancel: '\uCDE8\uC18C',
    noResults: '\uACB0\uACFC \uC5C6\uC74C', loading: '\uB85C\uB529 \uC911...', welcome: '\uD658\uC601\uD569\uB2C8\uB2E4',
    password: '\uBE44\uBC00\uBC88\uD638', signIn: '\uB85C\uADF8\uC778', language: '\uC5B8\uC5B4',
  },
  ar: { langName: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629', flag: '\u{1F1F8}\u{1F1E6}', dir: 'rtl',
    dashboard: '\u0644\u0648\u062D\u0629 \u0627\u0644\u0642\u064A\u0627\u062F\u0629', generator: '\u0627\u0644\u0645\u0648\u0644\u062F',
    gallery: '\u0627\u0644\u0645\u0639\u0631\u0636', mint: '\u0633\u0643 NFT',
    save: '\u062D\u0641\u0638', cancel: '\u0625\u0644\u063A\u0627\u0621',
    noResults: '\u0644\u0627 \u062A\u0648\u062C\u062F \u0646\u062A\u0627\u0626\u062C', loading: '\u062C\u0627\u0631\u064A \u0627\u0644\u062A\u062D\u0645\u064A\u0644...', welcome: '\u0645\u0631\u062D\u0628\u0627',
    password: '\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631', signIn: '\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644', language: '\u0627\u0644\u0644\u063A\u0629',
  },
  pt: { langName: 'Portugu\u00eas', flag: '\u{1F1E7}\u{1F1F7}', dir: 'ltr',
    dashboard: 'Painel', generator: 'Gerador', gallery: 'Galeria', mint: 'Cunhar NFT',
    save: 'Salvar', cancel: 'Cancelar', loading: 'Carregando...', welcome: 'Bem-vindo',
    password: 'Senha', signIn: 'Entrar', language: 'Idioma',
  },
  ru: { langName: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439', flag: '\u{1F1F7}\u{1F1FA}', dir: 'ltr',
    dashboard: '\u041F\u0430\u043D\u0435\u043B\u044C', generator: '\u0413\u0435\u043D\u0435\u0440\u0430\u0442\u043E\u0440',
    gallery: '\u0413\u0430\u043B\u0435\u0440\u0435\u044F', mint: '\u0427\u0435\u043A\u0430\u043D\u0438\u0442\u044C NFT',
    save: '\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C', cancel: '\u041E\u0442\u043C\u0435\u043D\u0430',
    loading: '\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...', welcome: '\u0414\u043E\u0431\u0440\u043E \u043F\u043E\u0436\u0430\u043B\u043E\u0432\u0430\u0442\u044C',
    password: '\u041F\u0430\u0440\u043E\u043B\u044C', signIn: '\u0412\u043E\u0439\u0442\u0438', language: '\u042F\u0437\u044B\u043A',
  },
};

// Detect browser language
const detectLanguage = () => {
  const saved = localStorage.getItem('app_language');
  if (saved && translations[saved]) return saved;
  const browser = navigator.language?.split('-')[0] || 'en';
  return translations[browser] ? browser : 'en';
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(detectLanguage);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('app_language', lang);
      document.documentElement.dir = translations[lang].dir || 'ltr';
      document.documentElement.lang = lang;
    }
  }, []);

  useEffect(() => {
    document.documentElement.dir = translations[language]?.dir || 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en?.[key] || key;
  }, [language]);

  return React.createElement(I18nContext.Provider, { value: { t, language, setLanguage, translations } }, children);
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    return {
      t: (key) => translations.en?.[key] || key,
      language: 'en',
      setLanguage: () => {},
      translations,
    };
  }
  return context;
};

// Drop-in Language Selector Component
export const LanguageSelector = ({ style, className }) => {
  const { language, setLanguage, translations: langs } = useTranslation();

  const selectorStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: 'var(--card-bg, #1e293b)',
    border: '1px solid var(--border, #334155)',
    borderRadius: '8px',
    color: 'var(--text, #e2e8f0)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    ...style,
  };

  return React.createElement('select', {
    value: language,
    onChange: (e) => setLanguage(e.target.value),
    style: selectorStyle,
    className: className || 'language-selector',
  }, Object.entries(langs).map(([code, lang]) =>
    React.createElement('option', { key: code, value: code },
      `${lang.flag} ${lang.langName}`
    )
  ));
};

const i18n = { I18nProvider, useTranslation, LanguageSelector };
export default i18n;
